import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Table as MuiTable,
  Paper,
  TableContainer,
} from '@mui/material';
import type { TableProps, Order, AdvancedTableProps } from './types';
import { ORDER_TYPES } from './constants';
import { CollapsibleCell } from './CollapsibleCell';
import { AdvancedPagination, Checkbox, SearchField } from '../';

export const Table = <T,>({
  headCells,
  data,
  refetch,
  showIndex = false,
  showCheckBox = false,
  checkBoxProps,
  handleRequestSort,
  handleDragRow,
  fontSize = 12,
  height = '80vh',
  minWidth = '1000px',
}: TableProps<T>): React.ReactNode => {
  const { checkedIndex, onChangeCheckedIndex, checkedAll, onCheckAll } =
    checkBoxProps ?? {
      checkedIndex: [],
      onChangeCheckedIndex: (checkedIndex: number[]) => {},
      checkedAll: false,
      onCheckAll: (checkAll: boolean) => {},
    };

  const [order, setOrder] = useState<Order>(ORDER_TYPES.ASC);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [orderBy, setOrderBy] = useState<keyof T>();
  const [focusedRowIndex, setFocusedRowIndex] = useState<number | null>(null);
  const [expandedRows, setExpandedRows] = useState<number[]>([]); // to store all index of expanded row(s)

  const visibleHeadCells = headCells.filter(cell => !cell.collapsed);
  const collapsedHeadCells = headCells.filter(cell => cell.collapsed);
  const collapsedColumns = collapsedHeadCells.length;
  const isCollapsibleTable = collapsedColumns > 0;
  const visibleColumns = visibleHeadCells.length + Number(isCollapsibleTable);
  const isTableRowDraggable = Boolean(handleDragRow);

  const handleDragStart = (index: number) => () => {
    setDraggingIndex(index);
  };

  const handleDragOver = (
    event: React.DragEvent<HTMLTableRowElement>,
  ): void => {
    event.preventDefault();
  };

  const handleDrop = (index: number) => () => {
    if (draggingIndex === null || draggingIndex === index) return;

    const updatedData = [...data];
    const draggedRow = updatedData[draggingIndex];
    updatedData.splice(draggingIndex, 1);
    updatedData.splice(index, 0, draggedRow);
    setDraggingIndex(null);
    if (handleDragRow) {
      handleDragRow(updatedData);
    }
  };

  const handleSort = (property: keyof T) => () => {
    const isOrderByChanged = orderBy !== property;
    if (isOrderByChanged && handleRequestSort) {
      setOrderBy(property);
      handleRequestSort(property);
    } else {
      const isAsc = order === ORDER_TYPES.ASC;
      setOrder(isAsc ? ORDER_TYPES.DESC : ORDER_TYPES.ASC);
    }
  };

  const handleRowClick = (index: number) => () => {
    setFocusedRowIndex(index === focusedRowIndex ? null : index);
  };

  const handleCheckAll = (): void => {
    if (checkedAll) {
      onChangeCheckedIndex([]);
    } else {
      const newCheckedRows = Array.from(
        { length: data.length },
        (_, index) => index,
      );
      onChangeCheckedIndex(newCheckedRows);
    }
    onCheckAll(!checkedAll);
  };

  const handleCheck = (index: number) => () => {
    const newCheckedRows = checkedIndex.includes(index)
      ? checkedIndex.filter((rowIndex: number) => rowIndex !== index)
      : [...checkedIndex, index];
    onChangeCheckedIndex(newCheckedRows);
    onCheckAll(newCheckedRows.length === data.length);
  };

  const handleExpand = (index: number) => () => {
    const newExpandedRows = expandedRows.includes(index)
      ? expandedRows.filter((rowIndex: number) => rowIndex !== index)
      : [...expandedRows, index];
    setExpandedRows(newExpandedRows);
  };

  const tableBody = useMemo(() => {
    return (
      <TableBody>
        {data.map((row, index) => {
          const isFocusedRow = focusedRowIndex === index;
          const rowChecked = checkedIndex.includes(index);
          return (
            <MuiTable
              key={index}
              sx={{
                backgroundColor: isFocusedRow ? '#F3E6E6' : '',
                width: '100%',
                '&:hover': {
                  backgroundColor: isFocusedRow ? '#F3E6E6' : '#f0f0f0',
                },
                margin: '2px',
              }}
              component={Paper}
              onClick={handleRowClick(index)}
            >
              <CollapsibleCell<T>
                index={index}
                row={row}
                refetch={refetch}
                fontSize={fontSize}
                showIndex={showIndex}
                showCheckBox={showCheckBox}
                checked={rowChecked}
                isTableRowDraggable={isTableRowDraggable}
                visibleHeadCells={visibleHeadCells}
                collapsedHeadCells={collapsedHeadCells}
                handleDragStart={handleDragStart}
                handleDragOver={handleDragOver}
                handleDrop={handleDrop}
                handleCheck={handleCheck}
              />
            </MuiTable>
          );
        })}
      </TableBody>
    );
  }, [
    data,
    focusedRowIndex,
    isCollapsibleTable,
    expandedRows,
    handleRowClick,
    isTableRowDraggable,
    handleDragStart,
    handleDragOver,
    handleDrop,
    showIndex,
    showCheckBox,
    checkedIndex,
    handleCheck,
    visibleHeadCells,
    visibleColumns,
    handleExpand,
    collapsedHeadCells,
    collapsedColumns,
  ]);

  return (
    <StyledTableContainer
      sx={{
        height,
      }}
    >
      <MuiTable style={{ minWidth }}>
        <TableHead className="table-header-area">
          <TableRow sx={{ width: '100%' }}>
            {showCheckBox && (
              <TableCell
                align="center"
                style={{
                  marginLeft: 15,
                }}
              >
                <Checkbox
                  size="small"
                  value={checkedAll}
                  onChange={handleCheckAll}
                  label=""
                  sx={{
                    '& .MuiSvgIcon-root': {
                      '& path': {
                        fill: 'white',
                      },
                    },
                  }}
                />
              </TableCell>
            )}

            {showIndex && <TableCell align="center" width={'2%'} />}

            {visibleHeadCells.map(headCell => {
              const minCellWidth: number =
                headCell.minWidth ?? 100 / visibleColumns;
              const width = headCell.width ?? `${minCellWidth}%`;
              return (
                <TableCell
                  align="center"
                  key={headCell.id as string}
                  style={{
                    width,
                    fontSize: 14,
                    ...headCell.customStyle,
                  }}
                >
                  {headCell.isSortable ? (
                    <TableSortLabel
                      active={Boolean(headCell.id) && orderBy === headCell.id}
                      direction={
                        orderBy === headCell.id ? order : ORDER_TYPES.ASC
                      }
                      onClick={handleSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              );
            })}
            {isCollapsibleTable && (
              <TableCell
                align="center"
                key={'Arrow-Icon'}
                style={{
                  width: `4%`,
                }}
              >
                More
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        {tableBody}
      </MuiTable>
    </StyledTableContainer>
  );
};

export const AdvancedTable = <T,>(
  props: AdvancedTableProps<T>,
): React.ReactNode => {
  const {
    tableProps,
    searchFieldProps,
    advancedPaginationProps,
    showSearchField,
    showCheckBox = false,
    checkBoxProps,
    extendFunctionAreas,
    handleChangePage,
  } = props;

  const { headCells, data, handleRequestSort, handleDragRow } = tableProps;
  const { checkedIndex, onChangeCheckedIndex, checkedAll, onCheckAll } =
    checkBoxProps ?? {
      checkedIndex: [],
      onChangeCheckedIndex: (checkedIndex: number[]) => {},
      checkedAll: false,
      onCheckAll: (checkAll: boolean) => {},
    };
  const {
    placeholder,
    content = '',
    handleContentChange = () => {},
    handleSearch = () => {},
  } = searchFieldProps ?? {};

  const {
    page,
    count,
    recordText,
    rowsPerPageOptions,
    rowsPerPage,
    onChangeRowsPerPage,
  } = advancedPaginationProps;

  const handleChangeCheckedIndex = (checkedIndex: number[]): void => {
    onChangeCheckedIndex(checkedIndex);
  };

  const handleChangeCheckAll = (checkAll: boolean): void => {
    onCheckAll(checkAll);
  };
  return (
    <AdvancedTableContainer>
      <div className="mb-2">
        <div>{extendFunctionAreas}</div>
        <div>
          {showSearchField && (
            <SearchField
              placeholder={placeholder}
              content={content}
              handleSearch={handleSearch}
              handleContentChange={handleContentChange}
            />
          )}
        </div>
      </div>
      <div className="mb-8">
        <Table<T>
          showCheckBox={showCheckBox}
          checkBoxProps={{
            checkedIndex,
            onChangeCheckedIndex: handleChangeCheckedIndex,
            checkedAll,
            onCheckAll: handleChangeCheckAll,
          }}
          headCells={headCells}
          data={data}
          handleRequestSort={handleRequestSort}
          handleDragRow={handleDragRow}
        />
      </div>
      <div className="pagination-area">
        <AdvancedPagination
          page={page}
          count={count}
          recordText={recordText}
          rowsPerPageOptions={rowsPerPageOptions}
          rowsPerPage={rowsPerPage}
          onChange={handleChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
        />
      </div>
    </AdvancedTableContainer>
  );
};

const StyledTableContainer = styled(TableContainer)`
  overflow-y: auto;
  .MuiTable-root {
    table-layout: fixed;
    min-width: 1000px;
  }
  .table-header-area {
    position: sticky;
    top: 0;
    z-index: 1;
  }
  .MuiTableHead-root {
    display: flex;
    justify-content: center;

    tr {
      background-color: #951d1d;
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    th {
      color: #fff;
      border-bottom: none;
      padding: 1rem 0;
    }
  }

  .MuiTableBody-root {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    tr {
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    td {
      border-bottom: none;
      word-break: break-all;
      word-wrap: break-word;
    }
  }

  .Mui-active {
    color: rgb(255 219 33 / 87%) !important;
    font-weight: bold;

    svg {
      opacity: 1 !important;
    }
  }
`;

const AdvancedTableContainer = styled.div`
  .pagination-area {
    position: sticky;
    bottom: 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    text-align: center;
    width: 100%;
    padding: 0.5rem 0;
    background-color: #ffffff;
    margin-bottom: 10rem;
  }
`;
